<script setup>
import {computed, ref} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useSelectMenuOptionsStore} from '@/stores/selectMenuOptions'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useFormStore} from '@/stores/form'
import vFormValidation from '@/vue/directives/formValidation'
import FormControlPlainText from "@/common/form-control/FormControlPlainText.vue"

const props = defineProps({
    formId: String,
    currentData: Object,
    field: Object,
    options: [String, Array, Object],
})

// Pinia Stores
const componentsStore = useComponentsStore()
const selectMenuOptionsStore = useSelectMenuOptionsStore()
const componentStructureStore = useComponentStructureStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// Reactive internal options state
const optionsArray = ref(props.options || undefined)

// Computed properties
const components = computed(() => {
    return componentsStore.items
})

const componentIds = computed(() => {
    return componentsStore.componentIds
})

const tableName = computed(() => {
    return formStore.value?.tableName
})

const formSmall = computed(() => {
    return formStore.value?.formSmall
})

// Utility functions required by computed properties/methods
const getDefaultValue = (type, firstOption) => {
    const numericTypes = ['relationshipOneToMany']
    let defaultValue = ''

    if (!props.field.required) {
        if (numericTypes.indexOf(type) > -1) {
            defaultValue = 0
        } else if (firstOption && typeof firstOption.value === 'number') {
            defaultValue = 0
        }
    }

    return defaultValue
}

const firstOption = computed(() => {
    const currentFirst = optionsArray.value !== undefined ? optionsArray.value[0] : false

    if (currentFirst && (currentFirst.value === '' || currentFirst.value === 0)) {
        return false
    } else {
        return {
            value: getDefaultValue(props.field.type, currentFirst),
            text: props.field.firstOption || 'Select…'
        }
    }
})

// Methods conversions
const requestOptions = async (targetTableName, fieldName) => {
    if (targetTableName !== 'modulecomponents' && fieldName === 'navigationGroup') {
        targetTableName = 'pages'
        fieldName = 'navigation'
    }

    optionsArray.value = await selectMenuOptionsStore.get({
        tableName: targetTableName,
        fieldName,
        optionsArray: optionsArray.value
    })
}

const formatOptions = (options) => {
    let newOptions = []

    options.forEach((option, key) => {
        if (typeof key === 'string' && typeof option === 'object') {
            option.forEach((obj) => {
                newOptions.push({
                    value: obj.value,
                    text: obj.text,
                    group: key
                })
            })
        } else if (typeof key === 'string' && typeof option === 'string') {
            newOptions.push({
                value: key,
                text: option
            })
        } else if (
            (typeof key === 'string' || typeof key === 'number') &&
            (typeof option === 'string' || typeof option === 'number')
        ) {
            newOptions.push({
                value: key,
                text: option
            })
        } else if (typeof key === 'number' && typeof option === 'object') {
            newOptions.push({
                value: option.value,
                text: option.text,
                group: option.group
            })
        }
    })

    return newOptions
}

const setTableColumnOptions = (str) => {
    const parts = str.split(':')
    requestOptions(parts[1], parts[2])
}

const setFormBuilderOptions = () => {
    let objs = components.value.filter((obj) => obj.navigationGroup === 'Forms')
    optionsArray.value = objs.map((obj) => ({
        value: obj.tableName,
        text: obj.title
    }))
}

const setOrderByOptions = (targetTableName) => {
    let componentId = componentIds.value[targetTableName]
    let objs = componentStructureStore.get(componentId)

    objs = objs.filter((obj) => {
        return ['text', 'date', 'dateDate', 'datetime-local'].indexOf(obj.type) > -1
    })

    let component = components.value.find(o => o.id === componentId)
    if (component?.showDisplayOrder) {
        objs.push({
            columnName: 'displayOrder',
            title: 'Display order'
        })
    }

    objs = objs.sort((a, b) => (a.title > b.title ? 1 : -1))

    let options = []
    objs.forEach((obj) => {
        options.push({value: obj.columnName + ' ASC', text: obj.title})
        options.push({value: obj.columnName + ' DESC', text: obj.title + ' (desc)'})
    })

    options.push({value: 'RAND()', text: 'Random'})
    optionsArray.value = options
}

const setCategoryListOrderByOptions = () => {
    optionsArray.value = [
        {value: '', text: ' - none - '},
        {value: 'displayOrder', text: 'Display order'},
        {value: '-displayOrder', text: 'Display order (desc)'},
        {value: 'title', text: 'Title'},
        {value: '-title', text: 'Title (desc)'},
        {value: 'RAND()', text: 'Random'}
    ]
}

const setTagComponentOptions = () => {
    let objs = components.value.filter((obj) => obj.showTags)
    optionsArray.value = objs.map((obj) => ({
        value: obj.tableName,
        text: obj.title
    }))
}

const setOptionsArrayFromDeprecatedOptionsString = () => {
    let options = []
    let str = props.field.options
    let array = str.trim('\n').split('\n')
    array.forEach((line) => {
        if (line.search('/:/')) {
            let parts = line.split(':')
            options.push({value: parts[0], text: parts[1]})
        } else {
            options.push({value: line, text: line})
        }
    })
    optionsArray.value = options
}

const preLink = () => {
    let value = props.currentData[props.field.name]

    if (props.field.name === 'content__component' && value) {
        formStore.value?.presetData({
            name: props.field.name,
            value: parseInt(value),
        })
    }

    if ((value === null || value === undefined) && !props.field.hidden) {
        formStore.value?.presetData({
            name: props.field.name,
            value: getDefaultValue(props.field.type),
        })
    }
}

// Emulating the created hook logic sequentially
preLink()

if (props.field.options?.length > 0) {
    if (typeof props.field.options === 'string') {
        setOptionsArrayFromDeprecatedOptionsString()
    } else if (typeof props.field.options === 'object') {
        if (props.field.options.length === 1 && props.field.options[0].text === '') {
            let val = props.field.options[0].value

            if (val.indexOf('COLUMN:') === 0) {
                setTableColumnOptions(val)
            } else if (val.indexOf('FORM_BUILDER') === 0) {
                setFormBuilderOptions()
            } else if (val.indexOf('ORDER_BY:') === 0) {
                let targetTable = val.split(':').pop()
                setOrderByOptions(targetTable)
            } else if (val === 'COMPONENTS_WITH_TAGS') {
                setTagComponentOptions()
            } else if (val === 'CATEGORY_LIST_ORDER_BY') {
                setCategoryListOrderByOptions()
            }
        } else {
            optionsArray.value = formatOptions(props.field.options)
            if (props.field.selectType === '1') {
                requestOptions(tableName.value, props.field.name)
            }
        }
    }
} else if (optionsArray.value === undefined) {
    if (tableName.value) {
        requestOptions(tableName.value, props.field.name)
    } else {
        let component = components.value.find(o => o.id === props.field.modulecomponentsID || o.id === props.field.modulecomponentsId)
        requestOptions(component?.tableName, props.field.name)
    }
}
</script>

<template>
    <FormControlPlainText v-if="field.readonly" :value="currentData[field.name]" :form-id="formId"/>
    <select v-else :id="field.id" v-model="currentData[field.name]" v-form-validation :name="field.name"
            :required="field.required" class="form-select w-auto"
            :class="{'is-invalid': field.error, 'form-select-sm': formSmall}">

        <option v-if="firstOption" :value="firstOption.value" :selected="field.required" :disabled="field.required">
            {{ firstOption.text }}
        </option>

        <option v-for="option in optionsArray" :key="option.value" :value="option.value">
            {{ option.text }}
        </option>
    </select>
</template>